import { BaseMap, type AreaOfWork } from '../Maps/Map.ts';
import { debounce } from '../Utils';
import { LocationFilterBase, type ExistingLocationFilter } from './LocationFilterBase';
import { AutocompleteDropdown } from '../modules/AutocompleteDropdown';

export class PoolSearchAreaMap extends LocationFilterBase {
    private map: BaseMap;
    private currentArea: AreaOfWork | undefined;
    private readonly autocomplete: AutocompleteDropdown;
    private readonly debouncedInput: () => void;

    constructor(
        form: HTMLFormElement,
        private readonly mapContainer: HTMLDivElement,
        existingFilter: ExistingLocationFilter
    ) {
        super(form, existingFilter);

        this.autocomplete = new AutocompleteDropdown(this.searchBar, () =>
            this.searchBar.dispatchEvent(new Event('change'))
        );
        this.debouncedInput = debounce(this.handleInput, 300);
        this.map = this.initMap();
        this.attachListeners();

        const existingAddress = existingFilter?.address ?? null;
        if (existingAddress) {
            this.searchBar.dispatchEvent(new Event('change'));
        } else {
            this.map.getBrowserLocation().then((location: GeolocationPosition | null) => {
                if (location) {
                    this.map.reverseLookup(location).then((locations) => {
                        if (locations && locations.length > 0) {
                            const [firstPick] = locations;
                            if (firstPick) {
                                this.searchBar.value = firstPick.formattedAddress;
                                this.searchBar.dispatchEvent(new Event('change'));
                            }
                        }
                    });
                }
            });
        }

        this.initResizeHandler();
        this.map.healthCheck().then(async (healthy) => {
            if (!healthy) {
                await this.reInitialize();
            }
        });
    }

    private attachListeners(): void {
        this.searchBar.addEventListener('change', this.handleSearchBarChange);
        this.searchBar.addEventListener('input', this.debouncedInput);
    }

    private initMap = () => {
        return new BaseMap(this.mapContainer);
    };

    private initResizeHandler(): void {
        const debouncedResize = debounce(this.reInitialize, 300);
        const resizeObserver = new ResizeObserver(debouncedResize);
        resizeObserver.observe(this.mapContainer);
    }

    private reInitialize = async (): Promise<void> => {
        const area = this.currentArea;
        this.map.map.destroy();
        this.map = this.initMap();
        if (area) {
            this.map.setArea(area);
            this.map.zoomToArea();
        }
    };

    protected override onRadiusCommit(value: number): void {
        this.map.resizeAreaTo(value);
        this.map.zoomToArea();
        if (this.currentArea) {
            this.currentArea.radiusMeters = value;
        }
    }

    private handleInput = async () => {
        const query = this.searchBar.value.trim();

        if (query === '') {
            this.autocomplete.clear();
            this.map.clearAll();
            this.map.zoomToGermany();
            this.clearSearch();
            this.currentArea = undefined;
            return;
        }

        const results = await this.map.search(query);

        if (!results || results.length === 0) {
            this.autocomplete.clear();
            return;
        }

        this.toggleDisabledStateSearchBar(false);
        this.autocomplete.showSuggestions(results.map((r) => r.displayText));
    };

    private handleSearchBarChange: EventListener = async (event) => {
        const target = event.target as HTMLInputElement;
        if (!target) {
            return;
        }
        const address = target.value;
        this.searchBar.value = address;

        if (address === '') {
            this.clearSearch();
            return;
        }

        const lookupResults = await this.map.lookup(address);

        for (const { latitude, longitude, formattedAddress } of lookupResults) {
            this.latitudeHidden.value = latitude.toString(10);
            this.longitudeHidden.value = longitude.toString(10);
            this.addressHidden.value = formattedAddress;

            this.map.clearAll();

            this.currentArea = {
                id: 666,
                wholeGermany: false,
                radiusMeters:
                    this.radiusHidden.valueAsNumber || parseInt(this.radiusHidden.value, 10),
                longitude,
                latitude,
                searchDescription: address
            };

            this.map.setArea(this.currentArea);
            this.map.zoomToArea();
        }
    };

    private toggleDisabledStateSearchBar(disabled: boolean): void {
        this.searchBar.disabled = disabled;
    }

    public destroy(): void {
        this.autocomplete.destroy();
    }
}
